/* ============================================================
   assets/primex-cart-legal.js   (tabs + "click the disabled button" hint)

   Controls the Primex cart drawer checkout:
   - B2B / B2C are collapsible tabs. Exactly ONE box, in either tab, must be
     ticked; a ticked tab shows a red check in its header.
   - The checkout button ([data-px-checkout]) LOOKS disabled (grey) until then.
     It is not natively disabled once this script runs, so a click on it can
     open the closed tabs and point the customer to what they have to do.
     (The markup ships with the native `disabled` attribute: if this script
     ever fails to load, checkout simply stays blocked.)
   - On checkout: saves the confirmation (+ order note) via cart/update.js,
     verifies the save, then goes to /checkout.

   Built to survive theme differences: no custom element timing, delegated
   capture-phase listeners, cart-empty read from the cart rows, and a
   MutationObserver that re-applies the state after the drawer re-renders.

   Debug: type  pxCartLegalDebug()  in the browser console.
   ============================================================ */
(() => {
  if (window.__pxCartLegalLoaded) return;
  window.__pxCartLegalLoaded = true;

  const SEL = 'primex-cart-legal';
  const seen = new WeakSet();
  let busy = false;
  let lastOpen = null;      // null = never touched, otherwise the list of open tabs, e.g. ['b2c']

  const all = (root, s) => Array.from(root.querySelectorAll(s));

  const formOf = (el) => {
    const id = el.dataset.formId;
    return (id && document.getElementById(id)) || el.closest('form');
  };
  const buttonsOf = (el) => {
    const form = formOf(el);
    return all(document, '[data-px-checkout]').filter(
      (b) => (form && b.form === form) || (el.parentElement && el.parentElement.contains(b))
    );
  };
  const choicesOf = (el) => all(el, '[data-px-choice]');
  const hasConsent = (el) => choicesOf(el).filter((i) => i.checked).length === 1;
  const cartIsEmpty = (el) => {
    const form = formOf(el);
    return !!form && !form.querySelector('.cart-item');
  };
  const legalFor = (btn) => all(document, SEL).find((el) => buttonsOf(el).includes(btn));

  // ---- tabs (each one opens / closes on its own) ----
  const setCardOpen = (card, open) => {
    card.classList.toggle('is-open', open);
    const tab = card.querySelector('[data-px-tab]');
    if (tab) tab.setAttribute('aria-expanded', open ? 'true' : 'false');
    const panel = card.querySelector('.px-legal__panel');
    if (panel) panel.inert = !open;            // closed panels can't be tabbed into
  };
  const openKinds = (el) => all(el, '[data-px-card].is-open').map((c) => c.dataset.pxCard);
  const setOpen = (el, kinds) => {
    all(el, '[data-px-card]').forEach((c) => setCardOpen(c, kinds.includes(c.dataset.pxCard)));
  };

  // ---- state: grey (blocked) or normal ----
  function sync() {
    all(document, SEL).forEach((el) => {
      if (!seen.has(el)) {                      // a fresh element (page load or drawer re-render)
        seen.add(el);
        choicesOf(el).forEach((i) => { i.checked = false; });      // never start ticked
        if (lastOpen !== null) setOpen(el, lastOpen);              // keep the tabs the customer had open
      }
      all(el, '[data-px-card]').forEach((card) => {
        const box = card.querySelector('[data-px-choice]');
        card.classList.toggle('is-ticked', !!(box && box.checked));
      });
      const blocked = busy || cartIsEmpty(el) || !hasConsent(el);
      buttonsOf(el).forEach((b) => {
        if (b.disabled) b.disabled = false;     // script is alive: clicks must reach us
        b.classList.toggle('is-disabled', blocked);
        b.setAttribute('aria-disabled', blocked ? 'true' : 'false');
      });
    });
  }

  const hideError = (el) => {
    const err = el.querySelector('.px-legal__error');
    if (err) err.hidden = true;
  };
  const showError = (el, msg) => {
    const err = el.querySelector('.px-legal__error');
    if (!err) return;
    err.textContent = msg;
    err.hidden = false;
  };

  // The customer clicked checkout without confirming: open every closed tab,
  // flash the tabs and say what to do.
  let nudgeTimer = null;
  function nudge(el) {
    setOpen(el, all(el, '[data-px-card]').map((c) => c.dataset.pxCard));
    lastOpen = openKinds(el);
    showError(el, el.dataset.errorText || 'Bitte bestätigen Sie den für Sie zutreffenden Bereich.');
    el.classList.remove('is-nudge');
    void el.offsetWidth;                         // restart the animation
    el.classList.add('is-nudge');
    clearTimeout(nudgeTimer);
    nudgeTimer = setTimeout(() => el.classList.remove('is-nudge'), 1400);
    if (el.scrollIntoView) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    const firstTab = el.querySelector('[data-px-tab]');
    if (firstTab) firstTab.focus({ preventScroll: true });
  }

  // ---- tick / untick (capture phase: works even if other code stops bubbling) ----
  const onTick = (e) => {
    const t = e.target;
    if (!t || !t.matches || !t.matches('[data-px-choice]')) return;
    const el = t.closest(SEL);
    if (!el) return;
    if (t.checked) choicesOf(el).forEach((i) => { if (i !== t) i.checked = false; });
    hideError(el);
    sync();
  };
  document.addEventListener('change', onTick, true);
  document.addEventListener('input', onTick, true);

  // ---- open / close a tab ----
  document.addEventListener('click', (e) => {
    const tab = e.target.closest && e.target.closest('[data-px-tab]');
    if (!tab) return;
    const el = tab.closest(SEL);
    const card = tab.closest('[data-px-card]');
    if (!el || !card) return;
    setCardOpen(card, !card.classList.contains('is-open'));
    lastOpen = openKinds(el);
  }, true);

  // ---- checkout ----
  async function run(el, button) {
    if (busy) return;
    const kind = choicesOf(el).filter((i) => i.checked)[0].value;     // 'b2b' | 'b2c'
    busy = true;
    hideError(el);
    button.setAttribute('aria-busy', 'true');
    sync();

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const attributes = {
      primex_customer_type: kind,
      primex_terms_accepted: 'yes',
      primex_information_read: 'yes',
      primex_legal_version: el.dataset.version || '',
      primex_confirmed_at: new Date().toISOString()
    };
    const payload = { attributes };
    const noteEl = document.getElementById('CartDrawer-Note');        // keep a just-typed note
    if (noteEl) payload.note = noteEl.value;

    const req = new AbortController();
    const timeout = setTimeout(() => req.abort(), 15000);
    try {
      const res = await fetch(root + 'cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: req.signal
      });
      if (!res.ok) throw new Error('cart update failed');
      const cart = await res.json();
      const saved = cart.attributes || {};
      if (!Object.entries(attributes).every(([k, v]) => saved[k] === v)) throw new Error('save not confirmed');
      window.location.assign(root + 'checkout');
    } catch (err) {
      showError(el, 'Bestätigung konnte nicht gespeichert werden. Bitte erneut versuchen.');
    } finally {
      clearTimeout(timeout);
      busy = false;
      button.removeAttribute('aria-busy');
      sync();
    }
  }

  // One entry point for click + submit on the checkout button.
  function activate(e, btn) {
    const el = legalFor(btn);
    if (!el) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    // Enter pressed inside a field (quantity, note) must never start checkout.
    const ae = document.activeElement;
    const implicit = e.type === 'click' && e.detail === 0 && ae && ae !== btn && ae.matches &&
      ae.matches('input, textarea, select');
    if (implicit || busy || cartIsEmpty(el)) return;
    if (!hasConsent(el)) { nudge(el); return; }
    run(el, btn);
  }
  document.addEventListener('click', (e) => {
    const btn = e.target.closest && e.target.closest('[data-px-checkout]');
    if (btn) activate(e, btn);
  }, true);
  document.addEventListener('submit', (e) => {
    const btn = e.submitter;
    if (btn && btn.matches && btn.matches('[data-px-checkout]')) activate(e, btn);
  }, true);

  // ---- keep the state right after re-renders / foreign scripts ----
  new MutationObserver(sync).observe(document.documentElement, {
    childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'hidden']
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sync);
  else sync();

  // ---- console helper ----
  window.pxCartLegalDebug = () => all(document, SEL).map((el) => ({
    form: (formOf(el) || {}).id || null,
    tabs: all(el, '[data-px-card]').map((c) => c.dataset.pxCard + (c.classList.contains('is-open') ? ':open' : ':closed')),
    ticked: choicesOf(el).filter((i) => i.checked).map((i) => i.value),
    hasConsent: hasConsent(el),
    cartIsEmpty: cartIsEmpty(el),
    buttonsFound: buttonsOf(el).length,
    buttonLooksDisabled: buttonsOf(el).map((b) => b.getAttribute('aria-disabled') === 'true'),
    buttonNativelyDisabled: buttonsOf(el).map((b) => b.disabled),
    busy
  }));
})();